import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { FormFields } from "./hrForm";
import { EmptyState, Field, LoadState, PrimaryButton, SecondaryButton } from "./hrComponents";
import { cardStyle, formatDate, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "./hrUi";

// DOC-01..03: one person's documents. The person (or HR) uploads; HR verifies. Files open through a
// short-lived private link, and the first number shown is masked unless the caller may see it.
export async function openSignedUrl(fetchUrl) {
  try {
    const res = await fetchUrl();
    if (res?.data?.url) window.open(res.data.url, "_blank", "noopener");
    else toast.error("Could not open the document");
  } catch (e) {
    toast.error(e.message || "Could not open the document");
  }
}

function UploadDrawer({ show, onHide, employeeId, types, onDone }) {
  const [values, setValues] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const type = types.find((t) => t.id === values.document_type_id);

  React.useEffect(() => {
    if (show) {
      setValues({});
      setFile(null);
    }
  }, [show]);

  const save = async () => {
    if (!file) return toast.error("Choose a file to upload");
    setSaving(true);
    try {
      const res = await hrmsService.uploadEmployeeDocument(employeeId, file, {
        document_type_id: values.document_type_id,
        expiry_date: type?.has_expiry ? values.expiry_date : undefined,
        doc_number: values.doc_number,
      });
      if (res?.success) {
        toast.success(res.message || "Uploaded");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Upload document"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !values.document_type_id}>
            Upload
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        fields={[
          { key: "document_type_id", label: "Document type", type: "select", required: true, options: types.map((t) => ({ label: t.name, value: t.id })) },
          ...(type?.has_expiry ? [{ key: "expiry_date", label: "Expiry date", type: "date", required: true }] : []),
          { key: "doc_number", label: "Document number", type: "text", max: 40 },
        ]}
      />
      <Field label="File" required hint="PDF, JPG, PNG or DOCX up to 10 MB">
        <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => setFile(e.target.files[0] || null)} />
      </Field>
    </SlidePanel>
  );
}

function VerifyDrawer({ doc, onHide, onDone }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  React.useEffect(() => setComment(""), [doc]);

  const decide = async (status) => {
    if (status === "REJECTED" && !comment.trim()) return toast.error("Please say why the document is rejected");
    setSaving(true);
    try {
      const res = await hrmsService.verifyEmployeeDocument(doc.id, status, comment.trim() || undefined);
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={!!doc}
      onHide={() => !saving && onHide()}
      title={doc ? `Verify ${doc.type_name}` : "Verify"}
      footer={
        <>
          <SecondaryButton onClick={() => decide("REJECTED")} disabled={saving} style={{ color: "#B91C1C", borderColor: "#B91C1C" }}>
            Reject
          </SecondaryButton>
          <PrimaryButton onClick={() => decide("VERIFIED")} disabled={saving}>
            Verify
          </PrimaryButton>
        </>
      }
    >
      {doc ? (
        <>
          <button type="button" className="btn btn-link px-0 mb-3" onClick={() => openSignedUrl(() => hrmsService.getEmployeeDocumentUrl(doc.id))}>
            Open {doc.file_name}
          </button>
          <Field label="Comment" hint="Required to reject">
            <textarea className="form-control" rows={3} maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} />
          </Field>
        </>
      ) : null}
    </SlidePanel>
  );
}

export default function EmployeeDocuments({ employeeId, canManage, isSelf }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getEmployeeDocuments(employeeId), [employeeId]);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const docs = data?.documents || [];
  const types = data?.types || [];

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div style={{ fontSize: 13, color: "#6B7280" }}>
          {data && data.missing_mandatory.length ? (
            <span style={{ color: "#92400E" }}>Missing: {data.missing_mandatory.map((m) => m.name).join(", ")}</span>
          ) : (
            "All required documents are on file"
          )}
          {data ? ` · ${data.completeness}% complete` : ""}
        </div>
        {canManage || isSelf ? <PrimaryButton onClick={() => setUploading(true)}>Upload document</PrimaryButton> : null}
      </div>

      {docs.length === 0 ? (
        <EmptyState title="No documents yet" />
      ) : (
        <div style={{ ...cardStyle, overflowX: "auto" }}>
          <table className="table mb-0" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                {["Document", "Number", "Expires", "Status", ""].map((h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{d.type_name}</div>
                    <div style={{ fontSize: 12, color: "#6B7280" }}>{d.file_name}</div>
                  </td>
                  <td style={tdStyle}>{d.doc_number || "—"}</td>
                  <td style={tdStyle}>{d.expiry_date ? formatDate(d.expiry_date) : "—"}</td>
                  <td style={tdStyle}>
                    <span style={statusBadge(d.status)}>{statusLabel(d.status)}</span>
                    {d.verify_comment ? <div style={{ fontSize: 12, color: "#6B7280" }}>{d.verify_comment}</div> : null}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                    {d.can_download ? (
                      <button type="button" className="btn btn-link btn-sm" onClick={() => openSignedUrl(() => hrmsService.getEmployeeDocumentUrl(d.id))}>
                        View
                      </button>
                    ) : null}
                    {canManage && !isSelf && d.status === "PENDING" ? (
                      <button type="button" className="btn btn-link btn-sm" onClick={() => setVerifying(d)}>
                        Verify
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <UploadDrawer show={uploading} onHide={() => setUploading(false)} employeeId={employeeId} types={types} onDone={reload} />
      <VerifyDrawer doc={verifying} onHide={() => setVerifying(null)} onDone={reload} />
    </LoadState>
  );
}
